import { TreeNode } from 'primeng/api'
import { ClientIPTreeCategory, ClientIPTreeCategoryType } from '../../client-ip-list/types/ClientIPListTree'

export interface IPEntriesTreeDto<T = any> {
  label: string
  data: T & { type: ClientIPTreeCategoryType }
  children: IPEntriesTreeDto<T>[]
}

function mapNodeData(node: any): any {
  switch (node.data.type) {
    case ClientIPTreeCategory.PROGRAM_ENROLLMENT:
      return {
        programId: node.data.programId,
        contractedGoal: node.data.contractedGoal,
        isTerminated: node.data.isTerminated,
        type: node.data.type,
      }
    case ClientIPTreeCategory.INDIVIDUAL_PLAN:
      return {
        name: node.label,
        description: node.data.description,
        contractedGoal: node.data.contractedGoal,
        type: node.data.type,
      }
    case ClientIPTreeCategory.GOAL:
      return {
        name: node.label,
        description: node.data.description,
        criterion: node.data.criterion,
        type: node.data.type,
      }
    case ClientIPTreeCategory.STEP:
      return {
        name: node.label,
        description: node.data.description,
        criterion: node.data.criterion,
        type: node.data.type,
      }
    default:
      throw new Error(`Unknown node type: ${node.data.type}`)
  }
}

export function buildSelectedTree(nodes: TreeNode[]): IPEntriesTreeDto[] {
  const map = new Map<string, IPEntriesTreeDto>()

  const mergeChildren = (a: IPEntriesTreeDto[], b: IPEntriesTreeDto[]): IPEntriesTreeDto[] => {
    const m = new Map<string, IPEntriesTreeDto>()
    for (const c of [...a, ...b]) {
      const key = c.label
      if (!m.has(key)) m.set(key, c)
      else {
        const existing = m.get(key)!
        existing.children = mergeChildren(existing.children, c.children)
      }
    }
    return [...m.values()]
  }

  const buildNode = (node: TreeNode): IPEntriesTreeDto => ({
    label: node.label!,
    data: mapNodeData(node),
    children: node.children?.map(buildNode) ?? [],
  })

  const addNodeWithAncestors = (node?: TreeNode) => {
    if (!node?.data) return

    let current = buildNode(node)

    let parent = node.parent
    while (parent?.data) {
      const key = parent.label!
      if (map.has(key)) {
        const existing = map.get(key)!
        existing.children = mergeChildren(existing.children, [current])
        current = existing
        break
      } else {
        current = {
          label: key,
          data: mapNodeData(parent),
          children: [current],
        }
      }
      parent = parent.parent
    }

    const topKey = current.label
    if (!map.has(topKey)) map.set(topKey, current)
    else {
      const existing = map.get(topKey)!
      existing.children = mergeChildren(existing.children, current.children)
    }
  }

  nodes.forEach(addNodeWithAncestors)
  return [...map.values()]
}
